import { useInterval } from 'client/utils/use-interval';
import dayjs from 'dayjs';
import { useEffect, useState } from 'preact/hooks';
import { MeetingRow } from 'server/types';
import { getMeetingState, getMeetingStatus } from 'shared/meeting-utils';

export function useMeetingState(
  meeting: Pick<MeetingRow, 'scheduledAt' | 'durationMinutes' | 'type' | 'zoomHasWaitingRoom'>,
) {
  const [state, setState] = useState(() => getMeetingState(meeting));

  /*
   * Configure a timeout method for the scheduled date of the meeting,
   * and trigger a re-render with the new state.
   */
  useInterval(
    () => {
      setState(getMeetingState(meeting));
    },
    // Run this block on each 10 seconds
    // if the meeting is scheduled or about to start
    state.status === 'scheduled' || state.status === 'aboutToStart' ? 10000 : undefined,
  );

  return state;
}

export function useMeetingStatus({
  scheduledAt,
  durationMinutes,
}: Pick<MeetingRow, 'scheduledAt' | 'durationMinutes'>) {
  const [status, setStatus] = useState(getMeetingStatus({ scheduledAt, durationMinutes }));

  /*
     Updates the status when the draft meeting is scheduled
   */
  useEffect(() => {
    setStatus(getMeetingStatus({ scheduledAt, durationMinutes }));
  }, [scheduledAt, durationMinutes]);

  /*
   * Configure a timeout method for the scheduled date of the meeting,
   * and trigger a re-render with the new status.
   */
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (status === 'scheduled' || status === 'aboutToStart') {
      const diffInMs = dayjs(scheduledAt).diff(dayjs(), 'milliseconds');
      timer = setTimeout(() => {
        const newStatus = getMeetingStatus({ scheduledAt, durationMinutes });
        setStatus(newStatus);
      }, diffInMs);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [scheduledAt]);

  return status;
}
